(function () {
  const selectors = {
    root: '[data-predictive-search]',
    input: '[data-predictive-input]',
    panel: '[data-predictive-panel]',
    results: '[data-predictive-results]',
    status: '[data-predictive-status]',
    close: '[data-predictive-close]',
  };

  const cache = new Map();
  const moneyFormatterCache = new Map();

  class PredictiveSearch {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector(selectors.input);
      this.panel = root.querySelector(selectors.panel);
      this.results = root.querySelector(selectors.results);
      this.status = root.querySelector(selectors.status);
      this.closeButton = root.querySelector(selectors.close);
      this.currencyCode = root.dataset.currencyCode || 'USD';
      this.abortController = null;
      this.activeIndex = -1;
      this.items = [];

      if (!this.input || !this.panel || !this.results) return;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.root.addEventListener('submit', this.onSubmit.bind(this));
      document.addEventListener('click', this.onDocumentClick.bind(this));

      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => {
          this.close();
          this.input.blur();
        });
      }
    }

    onInput() {
      window.clearTimeout(this.inputTimer);
      this.inputTimer = window.setTimeout(() => {
        this.search(this.input.value.trim());
      }, 180);
    }

    onFocus() {
      if (this.results.children.length > 0 && this.input.value.trim().length > 1) {
        this.open();
      }
    }

    onSubmit(event) {
      if (!this.input.value.trim()) {
        event.preventDefault();
        this.input.focus();
      }
    }

    onDocumentClick(event) {
      if (!this.root.contains(event.target)) {
        this.close();
      }
    }

    onKeydown(event) {
      if (this.panel.hidden) return;

      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;

      this.items = Array.from(this.results.querySelectorAll('[data-predictive-item]'));
      if (this.items.length === 0) return;

      if (event.key === 'Enter') {
        if (this.activeIndex >= 0) {
          event.preventDefault();
          this.items[this.activeIndex].click();
        }
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.setActiveIndex((this.activeIndex + direction + this.items.length) % this.items.length);
    }

    async search(query) {
      this.activeIndex = -1;

      if (query.length < 2) {
        this.setStatus('Start typing');
        this.results.innerHTML = '';
        this.close();
        return;
      }

      if (cache.has(query)) {
        this.render(cache.get(query), query);
        return;
      }

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.setStatus('Searching');
      this.open();

      try {
        const response = await fetch(this.buildUrl(query), {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) throw new Error('Predictive search request failed');

        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        cache.set(query, products);
        this.render(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setStatus('Search unavailable');
        this.results.innerHTML = this.emptyMarkup('Try again in a moment.');
        this.open();
      }
    }

    buildUrl(query) {
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor',
      });

      return `${root}search/suggest.json?${params.toString()}`;
    }

    render(products, query) {
      if (products.length === 0) {
        this.setStatus('No matches');
        this.results.innerHTML = this.emptyMarkup(`No products found for "${query}".`);
        this.open();
        return;
      }

      this.setStatus(`${products.length} product${products.length === 1 ? '' : 's'}`);
      this.results.innerHTML = products.map((product) => this.productMarkup(product)).join('');
      this.open();
    }

    productMarkup(product) {
      const title = this.escapeHtml(product.title || 'Untitled product');
      const url = product.url || '/search';
      const image = product.image || product.featured_image || '';
      const price = this.formatPrice(product.price_min || product.price || product.price_max);
      const available = product.available === false ? 'Sold out' : 'View product';
      const imageMarkup = image
        ? `<img src="${this.escapeAttribute(image)}" alt="" loading="lazy">`
        : `<span>${title.slice(0, 2)}</span>`;

      return `
        <a class="wl-predictive-search__item" href="${this.escapeAttribute(url)}" role="option" data-predictive-item>
          <span class="wl-predictive-search__thumb">${imageMarkup}</span>
          <span class="wl-predictive-search__meta">
            <span class="wl-predictive-search__title">${title}</span>
            <span class="wl-predictive-search__price">${price}</span>
          </span>
          <span class="wl-predictive-search__cta">${available}</span>
        </a>
      `;
    }

    emptyMarkup(message) {
      return `<div class="wl-predictive-search__empty">${this.escapeHtml(message)}</div>`;
    }

    setActiveIndex(index) {
      this.items.forEach((item) => item.removeAttribute('aria-selected'));
      this.activeIndex = index;
      this.items[this.activeIndex].setAttribute('aria-selected', 'true');
      this.items[this.activeIndex].scrollIntoView({ block: 'nearest' });
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.root.classList.add('is-open');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.root.classList.remove('is-open');
      this.activeIndex = -1;
    }

    formatPrice(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return '';

      const key = `${document.documentElement.lang || 'en'}-${this.currencyCode}`;
      if (!moneyFormatterCache.has(key)) {
        moneyFormatterCache.set(
          key,
          new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: this.currencyCode,
          })
        );
      }

      return moneyFormatterCache.get(key).format(amount);
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value;
      return div.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/"/g, '&quot;');
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(selectors.root).forEach((root) => new PredictiveSearch(root));
  });
})();
